import { ref, computed, readonly } from 'vue'
import { apiClient } from '@/api'

export interface PromotionInfo {
  code: string
  promotionCodeId: string
  name: string | null
  percentOff: number | null
  amountOff: number | null
  amountOffCurrency: string | null
  duration: string | null
  durationInMonths: number | null
  expiresAt: number | null
}

export interface PricingData {
  priceId: string
  productName: string
  amount: number
  currency: string
  interval: string | null
  trialDays: number | null
  promotion: PromotionInfo | null
}

// Shared state — fetched once, used everywhere
const pricing = ref<PricingData | null>(null)
const isLoading = ref(false)
let fetchPromise: Promise<void> | null = null

async function fetchPricing() {
  if (pricing.value) return
  if (fetchPromise) return fetchPromise

  fetchPromise = (async () => {
    isLoading.value = true
    try {
      const response = await apiClient.get<PricingData>('/stripe/pricing')
      pricing.value = response.data
    } catch (e) {
      console.warn('Failed to fetch pricing:', e)
    } finally {
      isLoading.value = false
      fetchPromise = null
    }
  })()

  return fetchPromise
}

export function usePricing() {
  // Kick off fetch if not already loaded
  fetchPricing()

  const hasTrialDays = computed(() => (pricing.value?.trialDays ?? 0) > 0)

  const formattedPrice = computed(() => {
    if (!pricing.value) return ''
    const amount = pricing.value.amount / 100
    const currency = pricing.value.currency.toUpperCase()
    // Use Intl for proper currency formatting
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: pricing.value.currency,
      }).format(amount)
    } catch {
      return `${currency} ${amount.toFixed(2)}`
    }
  })

  const formattedInterval = computed(() => {
    if (!pricing.value?.interval) return ''
    const map: Record<string, string> = {
      month: 'month',
      year: 'year',
      week: 'week',
      day: 'day',
    }
    return map[pricing.value.interval] ?? pricing.value.interval
  })

  /** e.g. "$9.99/month" */
  const priceLabel = computed(() => {
    if (!formattedPrice.value) return ''
    if (!formattedInterval.value) return formattedPrice.value
    return `${formattedPrice.value}/${formattedInterval.value}`
  })

  /** CTA text: "Try Free for 14 Days" or "Subscribe for $9.99/month" */
  const ctaText = computed(() => {
    if (!pricing.value) return 'Get Pro'
    if (hasTrialDays.value) {
      return `Try Free for ${pricing.value.trialDays} Days`
    }
    return `Subscribe for ${priceLabel.value}`
  })

  /** Trial badge text: "Includes 14-day free trial" or null */
  const trialBadge = computed(() => {
    if (!hasTrialDays.value) return null
    return `Includes ${pricing.value!.trialDays}-day free trial`
  })

  /** Short trial text: "Start with a 14-day free trial" or null */
  const trialDescription = computed(() => {
    if (!hasTrialDays.value) return null
    return `Start with a ${pricing.value!.trialDays}-day free trial`
  })

  /** Promotion banner text, e.g. "35% off — Spring Sale!" or null */
  const promotionBanner = computed(() => {
    const promo = pricing.value?.promotion
    if (!promo) return null
    if (promo.percentOff) {
      return promo.name ? `${promo.percentOff}% off — ${promo.name}` : `${promo.percentOff}% off`
    }
    if (promo.amountOff) {
      const currency = promo.amountOffCurrency ?? pricing.value?.currency ?? 'usd'
      try {
        const formatted = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
        }).format(promo.amountOff / 100)
        return promo.name ? `${formatted} off — ${promo.name}` : `${formatted} off`
      } catch {
        return promo.name ?? 'Special offer'
      }
    }
    return promo.name ?? null
  })

  const hasPromotion = computed(() => !!pricing.value?.promotion)

  const promoCode = computed(() => pricing.value?.promotion?.code ?? null)

  return {
    pricing: readonly(pricing),
    isLoading: readonly(isLoading),
    hasTrialDays,
    formattedPrice,
    formattedInterval,
    priceLabel,
    ctaText,
    trialBadge,
    trialDescription,
    promotionBanner,
    hasPromotion,
    promoCode,
    fetchPricing,
  }
}
